import { useCallback, useRef, useState } from 'react'
import type { DragEvent } from 'react'

// Whether the drag carries OS files (vs. text selections or in-app drags).
const hasFiles = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files')

// Drop-target state for a container: spread `dropHandlers` on the element and
// show an overlay while `isDragActive`. dragenter/dragleave fire for every
// child crossed, so a depth counter decides when the drag truly left.
export function useFileDropZone(onFiles: (files: File[]) => void) {
  const [isDragActive, setIsDragActive] = useState(false)
  const depth = useRef(0)

  const onDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFiles(event)) return
    event.preventDefault()
    depth.current += 1
    setIsDragActive(true)
  }, [])

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFiles(event)) return
    // Without this the browser navigates to / opens the dropped file.
    event.preventDefault()
  }, [])

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFiles(event)) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setIsDragActive(false)
  }, [])

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      depth.current = 0
      setIsDragActive(false)
      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.length > 0) onFiles(files)
    },
    [onFiles],
  )

  return { isDragActive, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } }
}
